import React, { FC, useCallback, useMemo, useState } from 'react';
import { Button, InputNumber, Popconfirm, Switch, Tooltip } from 'antd';
import { DeleteOutlined, DownOutlined, PlusOutlined } from '@ant-design/icons';
import { ColorPicker } from '../LabelCreation/ColorPicker';
import {
	getWalkthroughHeatMapRangeLabel,
	getWalkthroughHeatMapRanges,
	MIN_WALKTHROUGH_HEAT_MAP_RANGES,
	normalizeWalkthroughHeatMapRanges,
} from '../../utils/WalkthroughHeatMapRangeUtils';
import { WalkthroughHeatMapRange } from '../../types/walkthrough_heat_map_ranges';
import WalkthroughHeatMapLegend from './WalkthroughHeatMapLegend';

interface WalkthroughHeatMapRangesFieldProps {
	// Supplied by the enclosing antd Form.Item. `null` means "not customized here".
	value?: WalkthroughHeatMapRange[] | null;
	onChange?: (value: WalkthroughHeatMapRange[] | null) => void;
	disabled?: boolean;
	// Explains where these colors apply, which differs by where this is used.
	description?: string;
	// When true the rows stay collapsed behind a switch and `null` is a meaningful saved value —
	// used when creating a site survey, where the template's standard already applies and the rows
	// are only worth showing if the user intends to override them. When false (editing an existing
	// record) the rows are always visible, seeded from `value` or the platform defaults, so the
	// editor never opens on an empty state.
	toggleable?: boolean;
	toggleLabel?: string;
	// Rendered on the left of the toggle row, mirroring SelectWithAllToggle's "Select All" header.
	// A toggleable call site passes its field label here instead of to the enclosing Form.Item, so
	// the label and the switch share one row rather than stacking.
	label?: string;
	// The standard these rows fall back to — the selected template's colors when creating a site
	// survey. Seeds the rows when the switch is turned on and backs "Reset".
	inheritedRanges?: WalkthroughHeatMapRange[] | null;
}

// Returns the range a new one should be inserted above, or -1 when nothing has room left.
//
// Scans from the top down so a new tier lands as high up the scale as it will fit — "Add Zone"
// then puts the new row in a predictable place instead of wherever the arithmetic happens to point.
// Walking downwards on the way matters: always splitting the topmost gap would crowd 87 / 93 / 96 /
// 99 into a corner and then refuse to add anything while the bottom of the scale sat empty.
//
// A gap must be at least 2 to qualify, since the new start has to be a distinct integer strictly
// between its neighbours.
const findInsertionIndex = (ranges: WalkthroughHeatMapRange[]): number => {
	for (let index = ranges.length - 1; index >= 0; index--) {
		const nextLowerRange = index === ranges.length - 1 ? 100 : ranges[index + 1].lowerRange;
		if (nextLowerRange - ranges[index].lowerRange > 1) {
			return index;
		}
	}
	return -1;
};

const WalkthroughHeatMapRangesField: FC<WalkthroughHeatMapRangesFieldProps> = ({
	value = null,
	onChange,
	disabled = false,
	description = '',
	toggleable = false,
	toggleLabel = 'Override Colors',
	label = '',
	inheritedRanges = null,
}) => {
	const isCustomized = Array.isArray(value);
	// What the rows fall back to: the inherited standard when there is one, else the platform
	// defaults. Also what "Reset" and turning the switch on seed from.
	const fallbackRanges = useMemo(
		() => getWalkthroughHeatMapRanges(inheritedRanges),
		[inheritedRanges]
	);
	const ranges = useMemo(
		() => (isCustomized ? getWalkthroughHeatMapRanges(value) : fallbackRanges),
		[isCustomized, value, fallbackRanges]
	);
	const [openColorPickerIndex, setOpenColorPickerIndex] = useState<number | null>(null);

	const emitRanges = useCallback(
		(nextRanges: WalkthroughHeatMapRange[]) => {
			if (onChange) {
				onChange(normalizeWalkthroughHeatMapRanges(nextRanges));
			}
		},
		[onChange]
	);

	const onResetRanges = useCallback(() => {
		if (onChange) {
			onChange(normalizeWalkthroughHeatMapRanges(fallbackRanges));
		}
	}, [onChange, fallbackRanges]);

	const onToggleCustomized = useCallback(
		(checked: boolean) => {
			if (!onChange) {
				return;
			}
			// Off stores null rather than a copy of the standard, so the survey keeps tracking its
			// template instead of freezing whatever it happened to be showing.
			onChange(checked ? normalizeWalkthroughHeatMapRanges(fallbackRanges) : null);
		},
		[onChange, fallbackRanges]
	);

	const onChangeRangeStart = useCallback(
		(index: number, lowerRange: number) => {
			if (lowerRange === null || lowerRange === undefined) {
				return;
			}
			emitRanges(
				ranges.map((range, rangeIndex) => (rangeIndex === index ? { ...range, lowerRange } : range))
			);
		},
		[ranges, emitRanges]
	);

	const onChangeRangeColor = useCallback(
		(index: number, color: string) => {
			setOpenColorPickerIndex(null);
			emitRanges(
				ranges.map((range, rangeIndex) => (rangeIndex === index ? { ...range, color } : range))
			);
		},
		[ranges, emitRanges]
	);

	const onRemoveRange = useCallback(
		(index: number) => {
			setOpenColorPickerIndex(null);
			emitRanges(ranges.filter((_, rangeIndex) => rangeIndex !== index));
		},
		[ranges, emitRanges]
	);

	// No count limit — the only thing that can stop you adding a range is running out of room
	// between two existing starts.
	const insertionIndex = useMemo(() => findInsertionIndex(ranges), [ranges]);
	const canAddRange = insertionIndex !== -1;

	const onAddRange = useCallback(() => {
		if (!canAddRange) {
			return;
		}
		const anchorRange = ranges[insertionIndex];
		const nextLowerRange =
			insertionIndex === ranges.length - 1 ? 100 : ranges[insertionIndex + 1].lowerRange;
		const newLowerRange =
			anchorRange.lowerRange + Math.floor((nextLowerRange - anchorRange.lowerRange) / 2);
		emitRanges([...ranges, { color: anchorRange.color, lowerRange: newLowerRange }]);
	}, [ranges, canAddRange, emitRanges, insertionIndex]);

	// Collapsed-and-off is only a state the create form has; everywhere else the rows always show.
	const rowsVisible = !toggleable || isCustomized;

	return (
		<div className="flex flex-col gap-3">
			{toggleable ? (
				<div className="flex w-full flex-row items-center justify-between">
					<span>{label}</span>
					<div className="flex flex-row items-center">
						<span className="mr-2">{toggleLabel}</span>
						<Switch
							size="small"
							checked={isCustomized}
							disabled={disabled}
							onChange={onToggleCustomized}
						/>
					</div>
				</div>
			) : null}
			{description ? (
				<div className="footnoteregular text-character-secondary">{description}</div>
			) : null}
			{/* Collapsed, the colors still show read-only so it is clear what the switch would override.
			    "No score" is left out — it is fixed, so it is not one of the colors on offer here. */}
			{!rowsVisible ? (
				<WalkthroughHeatMapLegend ranges={fallbackRanges} includeNoScore={false} />
			) : null}
			{rowsVisible ? (
				<>
					<div className="flex flex-col">
						{ranges.map((range, index) => {
							const isFirstRange = index === 0;
							const isLastRange = index === ranges.length - 1;
							// Bounding each start between its neighbours makes gaps and overlaps impossible,
							// so this editor never needs a validation error.
							const lowestAllowedStart = isFirstRange ? 0 : ranges[index - 1].lowerRange + 1;
							const highestAllowedStart = isLastRange ? 100 : ranges[index + 1].lowerRange - 1;

							return (
								<div
									key={`range-${index}`}
									className="flex items-center gap-3 border-0 border-b border-solid border-grey-4 py-3 last:border-b-0"
								>
									<ColorPicker
										currentColor={range.color}
										onColorChange={(color) => onChangeRangeColor(index, color)}
										onCancel={() => setOpenColorPickerIndex(null)}
										visible={openColorPickerIndex === index}
										onVisibleChange={(open) => setOpenColorPickerIndex(open ? index : null)}
										buttonDisplay={
											<span className="inline-flex h-8 shrink-0 cursor-pointer items-center gap-2 rounded-[2px] border border-solid border-grey-4 bg-white px-2 shadow-[0px_2px_0px_rgba(0,0,0,0.02)]">
												<span
													className="h-4 w-4 rounded-[2px]"
													style={{ backgroundColor: range.color }}
												/>
												<DownOutlined className="text-[10px] text-grey-7" />
											</span>
										}
									/>
									<InputNumber
										min={lowestAllowedStart}
										max={highestAllowedStart}
										precision={0}
										value={range.lowerRange}
										// The lowest range must start at 0 so every score is covered by some color.
										disabled={disabled || isFirstRange}
										onChange={(lowerRange) => onChangeRangeStart(index, lowerRange as number)}
										style={{ width: 80 }}
									/>
									<span className="bodyregular text-character-secondary">
										{getWalkthroughHeatMapRangeLabel(range, index, ranges)}
									</span>
									<Tooltip
										title={
											ranges.length <= MIN_WALKTHROUGH_HEAT_MAP_RANGES
												? `Keep at least ${MIN_WALKTHROUGH_HEAT_MAP_RANGES} ranges`
												: 'Remove this range'
										}
									>
										<Button
											className="ml-auto shrink-0"
											type="text"
											icon={<DeleteOutlined />}
											disabled={disabled || ranges.length <= MIN_WALKTHROUGH_HEAT_MAP_RANGES}
											onClick={() => onRemoveRange(index)}
										/>
									</Tooltip>
								</div>
							);
						})}
					</div>
					<div className="flex items-center gap-3">
						<Button
							icon={<PlusOutlined />}
							disabled={disabled || !canAddRange}
							onClick={onAddRange}
						>
							Add Zone
						</Button>
						<Popconfirm
							title={
								inheritedRanges
									? "Replace these colors with the template's? Any changes here will be lost."
									: 'Replace these colors with the defaults? Any changes here will be lost.'
							}
							okText="Reset"
							cancelText="Cancel"
							disabled={disabled}
							onConfirm={onResetRanges}
						>
							<Button type="link" disabled={disabled}>
								{inheritedRanges ? 'Reset to template' : 'Reset to default'}
							</Button>
						</Popconfirm>
					</div>
				</>
			) : null}
		</div>
	);
};

export default WalkthroughHeatMapRangesField;
